import React, { MutableRefObject, useImperativeHandle, useMemo } from 'react';
import { ModalsKeys, Nullable } from 'global';
import { useModal } from 'hooks/useModal';
import { Button, Stack } from '@mui/material';
import { grey } from '@mui/material/colors';
import { FormProvider } from 'react-hook-form';
import { AddCircle } from '@mui/icons-material';
import { Equipment } from 'modules/desking/types';
import { ChargeAttachType } from 'modules/equipment/types';
import { ListingForm } from 'components/shared/TableListing/types';
import { Listing, useListing } from 'components/shared/TableListing';
import {
  chargeTypesByLabels,
  generateEquipmentName,
  getCalculatedTotalsByLevel,
  newChargeRow,
  useEquipmentActions,
} from 'modules/equipment';
import { invoiceDetailsListingColumns } from './invoiceDetailsListingConfig';
import {
  buildBadgeLabel,
  mapQuoteEquipmentToSync,
  mapEquipmentToInvoiceTable,
  checkingEquipmentUpgradeNeeds,
  mapEquipmentAndQuoteListToInvoiceTable,
} from '../../../lib';
import {
  Quote,
  QuoteChargeType,
  CalculatedTotalsByLevel,
  QuoteChargeListingEntity,
  QuoteEquipmentWithCharge,
  QuoteChargeListingContext,
} from '../../../types';
import { QuoteFeeType } from '@trnsact/trnsact-shared-types';

interface Props {
  VOId: string;
  dynamicsVOId: string;
  quote: Nullable<Quote>;
  equipmentList: Equipment[];
  onSubscribeTotals: (calc: CalculatedTotalsByLevel) => void;
  chargesSubmitRef: MutableRefObject<{ submit: () => ListingForm<QuoteChargeListingEntity> } | null>;
}

export const InvoiceDetailsListing = ({
  VOId,
  quote,
  dynamicsVOId,
  chargesSubmitRef,
  onSubscribeTotals,
  equipmentList = [],
}: Props) => {
  const { onDeleteEquipment, onUpdateEquipment, onCreateEquipment, isAnyActionInProgress } = useEquipmentActions();

  const { handleOpen: handleOpenEquipmentDialog, handleClose: handleCloseEquipmentDialog } = useModal(
    ModalsKeys.CreateUpdateEquipmentDialog
  );
  const { handleOpen: onOpenCreateChargeDialog } = useModal(ModalsKeys.CreateChargesEquipmentDialog);
  const { handleOpen: handleOpenChargesDialog, handleClose } = useModal(ModalsKeys.ChargesEquipmentDialog);
  const { handleOpen: handleOpenConfirmationDialog } = useModal(ModalsKeys.Confirmation);

  const initialFields = useMemo(() => {
    if (!equipmentList.length) return [];

    const fields = mapEquipmentAndQuoteListToInvoiceTable(VOId, equipmentList, quote);
    onSubscribeTotals?.(getCalculatedTotalsByLevel(fields));
    return fields;
  }, [VOId, equipmentList, onSubscribeTotals, quote]);

  const {
    columns,
    handleAddRow,
    handleInsertRow,
    tableData,
    form,
    handleRemoveRow,
    onRemove,
    handleUpdateRow,
    handleMoveRow,
  } = useListing<QuoteChargeListingEntity, QuoteChargeListingContext>(
    {
      headerColumns: invoiceDetailsListingColumns,
      bodyFields: initialFields,
    },
    {
      shouldUnregister: false,
      onSubscribe: value => onSubscribeTotals?.(getCalculatedTotalsByLevel(value)),
    }
  );

  useImperativeHandle(chargesSubmitRef, () => ({
    submit: () => form.getValues(),
  }));

  const handleAddCharge = () => {
    handleOpenChargesDialog({
      onOpenCreateChargeDialog: () => {
        onOpenCreateChargeDialog({
          onConfirm: newCharge => {
            handleAddRow({
              equipment: null,
              type: QuoteChargeType.Invoice,
              charge: {
                financeType: 'other',
                label: newCharge.name,
                type: QuoteFeeType.Other,
                ...newChargeRow,
                attachTo: { id: VOId, type: ChargeAttachType.Invoice },
              },
            });

            handleClose();
          },
        });
      },
      onConfirm: charges => {
        handleClose();
        charges.forEach(charge =>
          handleAddRow({
            charge: {
              ...charge,
              type: charge.type,
              label: charge.label,
              financeType: chargeTypesByLabels[charge.label],
              attachTo: { id: VOId, type: ChargeAttachType.Invoice },
            },
            equipment: null,
            type: QuoteChargeType.Invoice,
          })
        );
      },
    });
  };

  const handleAddEquipment = () => {
    handleOpenEquipmentDialog({
      equipmentId: '',
      onCancel: handleCloseEquipmentDialog,
      onSubmit: async ({ createDTO }) => {
        handleCloseEquipmentDialog();

        const equipmentId = await onCreateEquipment({
          vendorOpportunityId: VOId,
          dynamicsVendorOpportunityId: dynamicsVOId,
          ...createDTO,
        });

        handleInsertRow(mapEquipmentToInvoiceTable({ equipmentId, ...createDTO }), 0);
      },
    });
  };

  const handleEditEquipment = (equipmentId: string, rowIndex: number) => {
    handleOpenEquipmentDialog({
      equipmentId,
      onCancel: handleCloseEquipmentDialog,
      onSubmit: async ({ updateDTO }) => {
        handleCloseEquipmentDialog();

        await onUpdateEquipment({
          vendorOpportunityId: VOId,
          dynamicsVendorOpportunityId: dynamicsVOId,
          ...updateDTO,
        });

        handleUpdateRow(mapEquipmentToInvoiceTable(updateDTO), rowIndex);
      },
    });
  };

  const handleSyncEquipment = async (equipmentForUpdate: Nullable<QuoteEquipmentWithCharge>) => {
    if (!equipmentForUpdate || !equipmentForUpdate.equipmentId) return;

    const targetEquipment = equipmentList.find(({ equipmentId }) => equipmentId === equipmentForUpdate.equipmentId);

    if (!targetEquipment) return;

    const isNeedToUpdate = checkingEquipmentUpgradeNeeds(equipmentForUpdate, targetEquipment);

    if (!isNeedToUpdate) return;

    await onUpdateEquipment({
      ...mapQuoteEquipmentToSync(equipmentForUpdate),
      vendorOpportunityId: VOId,
      equipmentId: targetEquipment.equipmentId,
      attachments: targetEquipment.attachments,
      dynamicsVendorOpportunityId: dynamicsVOId,
    });
  };

  const handleRemoveEquipment = (equipmentId: string, rowIndex: number) => {
    onDeleteEquipment(
      {
        equipmentId,
        vendorOpportunityId: VOId,
        dynamicsVendorOpportunityId: dynamicsVOId,
      },
      () => {
        onRemove(rowIndex);
      }
    );
  };

  const handleLinkCharge = (attachId: string, currentIndex: number, linkIndex: number) => {
    const currentRow = form.getValues(`bodyFields.${currentIndex}`);
    if (!currentRow.charge) return;

    const targetEquipment = equipmentList.find(({ equipmentId }) => equipmentId === attachId);

    const updatedRow = {
      ...currentRow,
      charge: {
        ...currentRow.charge,
        attachTo: {
          id: attachId,
          type: ChargeAttachType.Equipment,
          description: targetEquipment ? buildBadgeLabel(generateEquipmentName(targetEquipment)) : '',
        },
      },
    };

    handleUpdateRow(updatedRow, currentIndex);
    handleMoveRow(currentIndex, linkIndex);
  };

  const handleUnLinkCharge = (index: number) => {
    handleOpenConfirmationDialog({
      title: 'Confirmation',
      message: 'Are you sure you want to unlink this charge?',
      onConfirm: () => {
        const currentRow = form.getValues(`bodyFields.${index}`);
        if (!currentRow.charge) return;

        const updatedRow = {
          ...currentRow,
          charge: {
            ...currentRow.charge,
            attachTo: {
              id: VOId,
              type: ChargeAttachType.Invoice,
            },
          },
        };

        handleUpdateRow(updatedRow, index);
        handleMoveRow(index, tableData.length - 1);
      },
    });
  };

  return (
    <FormProvider {...form}>
      <Listing<QuoteChargeListingEntity, QuoteChargeListingContext>
        tableData={tableData}
        columns={columns}
        injectContext={{
          form,
          onRemoveRow: handleRemoveRow,
          onLinkCharge: handleLinkCharge,
          onUnLinkCharge: handleUnLinkCharge,
          onEditEquipment: handleEditEquipment,
          onSyncEquipment: handleSyncEquipment,
          onRemoveEquipment: handleRemoveEquipment,
          isAnyEquipmentActionInProgress: isAnyActionInProgress,
        }}
        actionPanelBottom={() => (
          <Stack direction="row" gap="1rem" sx={{ paddingBottom: '0.5rem', borderBottom: `1px solid ${grey[300]}` }}>
            <Button onClick={handleAddCharge} startIcon={<AddCircle />} size="small">
              Add Invoice Item
            </Button>

            <Button onClick={handleAddEquipment} startIcon={<AddCircle />} size="small">
              Add equipment
            </Button>
          </Stack>
        )}
      />
    </FormProvider>
  );
};
